import type { UseQueryResult } from '@tanstack/react-query';

import type { QueryRouteBarrelTypes } from '@core/api';

import type { QueryError } from '../utils/Query';
import { type UseSlabQueryType, useSlabQuery } from './useSlabQuery';

type SlabFormQueryResult<TData> = Omit<UseQueryResult<TData, QueryError>, 'isLoading'> & {
  isLoading: boolean;
};

/**
 * useSlabQuery for data that seeds a Formik form, which reads initialValues only once: over stale
 * (e.g. invalidated by a save) cache, loading lasts until the post-mount response lands.
 */
export const useSlabFormQuery = <TKey extends keyof QueryRouteBarrelTypes>(
  routeBarrelKey: TKey,
  args: QueryRouteBarrelTypes[TKey]['args'],
  options?: UseSlabQueryType<TKey>['options'],
): SlabFormQueryResult<QueryRouteBarrelTypes[TKey]['returns']> => {
  const query = useSlabQuery(routeBarrelKey, args, { ...options, refetchOnMount: true });

  return { ...query, isLoading: query.isFetching && !query.isFetchedAfterMount };
};
